// @flow
import React from 'react'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Snackbar from '@material-ui/core/Snackbar'
import Select from '@material-ui/core/Select'
import { compare } from 'er2_web_utils'
import { getLayers, getLayer } from 'catena/er2_map_userlayers/js/utils'
import * as ids from '../constants/ids'
import { getActiveTool } from '../components/geo_bar_panel'

type Props = {
    actions: {
        onClickInlineHelp: Function,
        onGeoBarFetchErrorRendered: Function,
        onGeoBarResultRendered: Function,
        onGeoBarSubmitZonalStats: Function,
        onGeoBarSelectLayer: Function,
    },
    mapsources: object,
    theme?: object,
}

function isValid(tool) {
    return tool.targetLayer.length > 0 && tool.boundaryLayer.length > 0
}

function zonalStat(props) {
    const tool = getActiveTool(props)
    const layers = getLayers(props.mapsources)
    const targetLayer = layers.find(lyr => lyr.id === tool.targetLayer)
    const boundaryLayer = layers.find(lyr => lyr.id === tool.boundaryLayer)
    props.actions.onGeoBarSubmitZonalStats(targetLayer, boundaryLayer)
}

function targetLayerFilter(lyr, props) {
    if (!lyr.layer_type || lyr.layer_type.toLowerCase() !== 'raster') {
        return false
    }
    return true
}

function boundaryLayerFilter(lyr, props) {
    const tool = getActiveTool(props)
    if (!lyr.layer_type || lyr.layer_type.toLowerCase() === 'raster') {
        return false
    }
    return true
}

class GeoBarZonalStat extends React.Component<Props> {
    renderNotifications(theme) {
        const tool = getActiveTool(this.props)
        const layerName = tool.result ? tool.result.layerName : ''
        return (<div>
            <Snackbar
                open={tool.resultRendered === false}
                autoHideDuration={6000}
                onClose={() => {
                    this.props.actions.onGeoBarResultRendered(tool.id)
                    if (this.props.actions.onUpdateUserLayers) this.props.actions.onUpdateUserLayers()
                }}
                message={<span id="message-id">Result saved to {layerName}</span>}
            />
            <Snackbar
                open={tool.fetchError}
                autoHideDuration={6000}
                onClose={() => this.props.actions.onGeoBarFetchErrorRendered(tool.id)}
                message={<span id="message-id">Error performing operation</span>}
            />
        </div>)
    }

    renderTool(theme) {
        const tool = getActiveTool(this.props)
        const layers = getLayers(this.props.mapsources)
        return (<React.Fragment>
            <div className={theme.controlGroup}>
                <FormControl
                    classes={{
                        root: theme.muiFormControl,
                    }}
                >
                    <InputLabel htmlFor={ids.GEO_BAR_TARGET_LAYER}>Target Layer</InputLabel>
                    <Select
                        value={tool.targetLayer}
                        inputProps={{
                            name: ids.GEO_BAR_TARGET_LAYER,
                            id: ids.GEO_BAR_TARGET_LAYER,
                        }}
                        onChange={evt =>
                            this.props.actions.onGeoBarSelectLayer(true, layers.find(lyr => lyr.id === evt.target.value),
                                tool, { kind: 'targetLayer' })}
                    >
                        {layers.filter(lyr => targetLayerFilter(lyr, this.props)).sort((a, b) => compare(a.name, b.name)).map(layer => (
                            <MenuItem key={layer.id} value={layer.id}>{layer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <div className={theme.controlGroup}>
                <FormControl
                    classes={{
                        root: theme.muiFormControl,
                    }}
                >
                    <InputLabel htmlFor={ids.GEO_BAR_BOUNDARY_LAYER}>Zonal Layer</InputLabel>
                    <Select
                        value={tool.boundaryLayer}
                        inputProps={{
                            name: ids.GEO_BAR_BOUNDARY_LAYER,
                            id: ids.GEO_BAR_BOUNDARY_LAYER,
                        }}
                        onChange={evt =>
                            this.props.actions.onGeoBarSelectLayer(true, layers.find(lyr => lyr.id === evt.target.value),
                                tool, { kind: 'boundaryLayer' })}
                    >
                        {layers.filter(lyr => boundaryLayerFilter(lyr, this.props)).sort((a, b) => compare(a.name, b.name)).map(layer => (
                            <MenuItem key={layer.id} value={layer.id}>{layer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <div className={theme.formGroup}>
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={!isValid(tool)}
                    onClick={() => zonalStat(this.props)}
                >
                    {!tool.isFetching &&
                    <span>Submit</span>
                    }
                    {tool.isFetching &&
                    <span>Running&nbsp;<i className="fa fa-refresh fa-spin" /></span>
                    }
                </Button>
            </div>
        </React.Fragment>)
    }

    render() {
        const theme = this.props.theme
        const tool = getActiveTool(this.props)
        const helpActive = tool.helpActive

        return (<div className={theme.geoBarCtr}>
            <div className={theme.formGroup}>
                <label className={theme.toolLabel}>Zonal Statistics Tool</label>
                <i
                    className={`${theme.helpIcon}
                    ${helpActive ? theme.helpIconActive : ''}
                    fa fa-question-circle`}
                    onClick={() => this.props.actions.onClickInlineHelp(ids.GEO_BAR_ZONAL_STATS)}
                    role="button"
                    tabIndex={0}
                />
            </div>
            {helpActive &&
            <div className={theme.helpInlineCtr}>
                <div className={theme.helpInline}>
                    <p>
                        Calculate zonal statistics for a raster layer.
                    </p>
                    <p>
                        To calculate zonal statistics, select a target layer (a raster file) and a zonal layer
                        (a vector layer) then click on Submit.  Zonal statistics will be calculated for the selected
                        layers, and the results will be displayed below.
                    </p>
                </div>
            </div>
            }
            <div className={theme.toolCtr}>
                {this.renderTool(theme)}
            </div>
            {this.renderNotifications(theme)}
        </div>)
    }
}

export default GeoBarZonalStat
